import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import useSectorService from '../../app/services/hooks/useSectorService';
import { Sector, SectorFilters, defaultSectorFilters } from '../../app/models/Sector';
import SectorDataTable from './components/tables/SectorDataTable';
import SectorEdit from './components/SectorEdit';
import SectorCreate from './components/SectorCreate';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    {
        name: 'Sectores',
        url: '/sectors',
        isActive: true
    }
];

const Sectors = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { fetchingGetSectors, getSectors, deleteSector } = useSectorService();
    const [sectors, setSectors] = useState<Sector[]>([]);
    const [sectorIdEdit, setSectorIdEdit] = useState<number>(-1);
    const [showingEdit, setShowingEdit] = useState(false);
    const [showingCreate, setShowingCreate] = useState(false);
    const [totalRows, setTotalRows] = useState<number>(0);
    const { navigationPage } = useNavigationPage();

    const [sectorFilter, setSectorFilter] = useState<SectorFilters>(defaultSectorFilters);

    useEffect(() => {
        reloadTable();
    }, [sectorFilter]);

    const getAllSectors = () => {
        getSectors(sectorFilter, {
            onSuccess: (response: ServiceResponse) => {
                setSectors(response.data.sectors);
                setTotalRows(response.data.total_rows);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message);
                if (response.data.to_dashboard) {
                    navigationPage('/');
                }
            }
        });
    };

    const showEdit = (sectorId: number) => {
        setShowingEdit(true);
        setSectorIdEdit(sectorId);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setSectorIdEdit(-1);
    };

    const showCreate = () => {
        setShowingCreate(true);
    };

    const hideCreate = () => {
        setShowingCreate(false);
    };

    const reloadTable = () => {
        getAllSectors();
    };

    const destroy = (sectorId: number) => {
        const _text = 'Está a punto de eliminar el sector #' + sectorId;

        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: _text,
            buttons: {
                confirmButton: {
                    label: 'Si, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando sector...');
                        deleteSector(sectorId, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().successAlert({
                                    title: 'Éxito',
                                    message: response.message
                                });

                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();

                                useReactConfirmAlert().errorAlert({
                                    title: 'Error',
                                    message: response.message
                                });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => {
                        setTimeout(() => {
                            useReactConfirmAlert().infoAlert({
                                title: 'Cancelado',
                                message: 'El sector no se ha eliminado.'
                            });
                        }, 0);
                    }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Sectores" breadcrumbs={breadcrumbs} />

            <DefaultCard>
                <DefaultToolBar
                    left={<ButtonCreate callbackCreate={showCreate} title="Nuevo sector" />}
                />
                <SectorDataTable
                    sectors={sectors}
                    edit={showEdit}
                    destroy={destroy}
                    loading={fetchingGetSectors}
                    filter={sectorFilter}
                    setFilter={setSectorFilter}
                    totalRows={totalRows}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate ? (
                <DefaultModal
                    show={showingCreate}
                    handleClose={hideCreate}
                    title="Crear Sector"
                    backdrop={true}
                    showFooter={false}
                >
                    <SectorCreate
                        onSaved={() => {
                            reloadTable();
                            hideCreate();
                        }}
                        onCancel={hideCreate}
                        onError={hideCreate}
                    />
                </DefaultModal>
            ) : null}

            {showingEdit ? (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar Sector"
                    backdrop={true}
                    showFooter={false}
                >
                    <SectorEdit
                        sectorId={sectorIdEdit}
                        onSaved={() => {
                            reloadTable();
                            hideEdit();
                        }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            ) : null}
        </>
    );
};

export default Sectors;
